import { Avatar, Button, Divider, Empty, Row, Skeleton, Space, Typography, theme } from 'antd';
import { AxiosError } from 'axios';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import useCurrentTicket from '../../../hooks/ticketDetailPage/useCurrentTicket';
import useTicketsByRequesterId from '../../../hooks/ticketDetailPage/useTicketsByRequesterId';
import { DetailedUser, NewTicket, TicketSystemFieldsKey, TicketTab, tabTypeMap } from '../../../model/types';
import { MergeTicketPostData, cancelMerge, getTicketById, mergeTicket } from '../../../services/tickets/tickets';
import { useTabStore } from '../../../store/RootStoreContext';
import { grispiNotification } from '../../../store/useNotificationStore';
import { getMaskedEmailForUI } from '../../../utils/helpers';
import requestWithMessage from '../../../utils/requestWithMessage';
import { pushTo } from '../../../utils/routeHelper';
import { filterSWRKeys } from '../../../utils/swr';
import { getTicketKey } from '../../../utils/ticketHelper';
import TicketCard from '../../TicketCard';
import { formatDate } from '../../TimeDisplay';

const styles = {
    radioStyle: {
        display: 'flex',
        height: '30px',
        lineHeight: '30px'
    },
    divider: {
        margin: 0,
        marginTop: '10px'
    },
    zeroMargin: {
        margin: 0,
        marginTop: 0
    },
    button: { marginTop: '2rem' }
};

const ticketMergeErrorMap = [
    { message: 'Ticket keys must be different', errorKey: 'merge_error_1' },
    { message: 'Source ticket is already merged', errorKey: 'merge_error_2' },
    { message: 'Source ticket is closed', errorKey: 'merge_error_3' },
    { message: 'Source ticket is invalid', errorKey: 'merge_error_4' },
    { message: 'Because its channel is not CALL', errorKey: 'merge_error_5' },
    { message: 'Because its call merge status is', errorKey: 'merge_error_6' },
    { message: 'TO_BE_DECIDED', errorKey: 'merge_error_7' },
    { message: 'is invalid. Because it', errorKey: 'merge_error_8' },
    { message: 'is not found', errorKey: 'merge_error_9' }
];

const emptyTicketKeyValue = '0';

interface TicketAssociationContainerProps {
    ticket: NewTicket;
    searchTerm?: string;
    actionAfterSubmit?: () => void;
}

const TicketAssociationContainer = ({ ticket, searchTerm = '' }: TicketAssociationContainerProps) => {
    const {
        query: { ticketKey }
    } = useRouter();
    const { t } = useTranslation('tickets');
    const {
        token: { padding }
    } = theme.useToken();
    const { mutate } = useCurrentTicket();
    const { mutate: globalMutate } = useSWRConfig();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { closeTabAndGetNewTargetUrl, tabs } = useTabStore();

    const [ticketKeyToAssociate, setTicketKeyToAssociate] = useState(emptyTicketKeyValue);
    const [loading, setLoading] = useState(false);

    const selectableTicketsFromTabs = tabs?.filter(
        (tab) => tab.type === tabTypeMap.TICKET && tab.key !== ticketKey && !tab.key.includes('newTicket')
    ) as TicketTab[];

    const ticketRequesterId = (ticket?.fieldMap?.[TicketSystemFieldsKey.requester]?.value as DetailedUser)?.id;
    const { data: associableTicketList, isLoading } = useTicketsByRequesterId(ticketRequesterId?.toString());

    const searchedSelectableTicketsFromTabs = selectableTicketsFromTabs.filter(
        (t) => t?.subject?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );

    const searchedAssociableTicketList = isLoading
        ? []
        : associableTicketList.filter((t) => {
              return t?.subject?.toLowerCase()?.includes(searchTerm?.toLowerCase());
          });

    useEffect(() => {
        ticketKeyToAssociate && buttonRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ticketKeyToAssociate]);

    const buttonText =
        ticketKeyToAssociate === emptyTicketKeyValue
            ? t('associateButtonText.dont')
            : t('associateButtonText.associate', {
                  ticketKey: ticketKeyToAssociate
              });

    const handleOnClick = async () => {
        setLoading(true);
        let startMessage: string;
        let finishMessage: string;

        if (ticketKeyToAssociate === emptyTicketKeyValue) {
            startMessage = t('message.onDontAssociateStart', { ticketKey });
            finishMessage = t('message.onDontAssociateFinish', { ticketKey });
            await requestWithMessage({
                key: 'associate-ticket',
                startMessage,
                finishMessage,
                apiRequest: async () => {
                    await cancelMerge(ticketKey as string);
                    await mutate();
                },
                errorConfig: {
                    onApiRequestError: (_error) => {
                        setLoading(false);
                    }
                }
            });
            return;
        }

        startMessage = t('message.onAssociateStart', { ticketKey, ticketKeyToAssociate });
        finishMessage = t('message.onAssociateFinish', { ticketKey, ticketKeyToAssociate });
        await requestWithMessage({
            key: 'associate-ticket',
            startMessage,
            finishMessage,
            apiRequest: async () => {
                const mergeTicketPostData: MergeTicketPostData = {
                    sourceKey: ticketKey as string,
                    targetKey: ticketKeyToAssociate,
                    resolution: 'MERGED_BY_WEB'
                };

                await mergeTicket(mergeTicketPostData);

                closeTabAndGetNewTargetUrl(ticketKey?.toString());
                await globalMutate(
                    filterSWRKeys(`/tickets/${ticketKeyToAssociate}?ui=true`),
                    getTicketById(ticketKeyToAssociate)
                );
                await pushTo(`/tickets/${ticketKeyToAssociate}`);
            },
            errorConfig: {
                onApiRequestError: (_error: AxiosError<Error>) => {
                    ticketMergeErrorMap.forEach((error) => {
                        if (_error.response.data.message.includes(error.message)) {
                            grispiNotification('error', t(`mergeErrors.${error.errorKey}`), 'error');
                        }
                    });
                    setLoading(false);
                },
                showAutoErrorMessage: false
            }
        });
        setLoading(false);
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <Space
                style={{ rowGap: '5px' }}
                direction="vertical"
                className="full-width"
                size="middle"
                id="TicketAssociationContainer"
            >
                {searchedSelectableTicketsFromTabs?.length > 0 ? (
                    <>
                        <Typography.Text strong>{t('selectTicket.fromTabs')}</Typography.Text>
                        {searchedSelectableTicketsFromTabs.map((tab, index) => (
                            <div key={tab?.key + 'div'} style={{ padding: `0 ${padding / 2}px` }}>
                                <TicketCard
                                    key={tab?.key + 'fromTab'}
                                    ticketKey={tab?.key}
                                    description={tab?.subject}
                                    title={getMaskedEmailForUI(tab?.requester)}
                                    isSelected={ticketKeyToAssociate === tab?.key}
                                    onClickAction={setTicketKeyToAssociate}
                                />

                                {index === searchedSelectableTicketsFromTabs.length - 1 ? null : (
                                    <Divider style={styles.zeroMargin} key={tab?.key + 'fromTab-divider'} />
                                )}
                            </div>
                        ))}
                        <Divider style={styles.divider}>
                            <Avatar>
                                <Typography.Text italic style={{ color: 'inherit' }}>
                                    {t('or')}
                                </Typography.Text>
                            </Avatar>
                        </Divider>
                    </>
                ) : null}
                <Typography.Text strong>
                    {t('selectTicket.available', {
                        requester: ticket.fieldMap[TicketSystemFieldsKey.requester]?.userFriendlyValue ?? '-'
                    })}
                </Typography.Text>

                {isLoading ? (
                    <Skeleton loading={isLoading} active avatar />
                ) : searchedAssociableTicketList?.length > 0 ? (
                    searchedAssociableTicketList.map((assignableTicket, index) => {
                        return (
                            <div key={getTicketKey(assignableTicket) + 'associable'}>
                                <TicketCard
                                    key={getTicketKey(assignableTicket) + 'associable'}
                                    ticketKey={getTicketKey(assignableTicket)}
                                    description={assignableTicket?.subject}
                                    title={`${t(formatDate(assignableTicket?.createdAt, {}) as any)}, ${assignableTicket
                                        ?.requester?.fullName}`}
                                    isSelected={ticketKeyToAssociate === getTicketKey(assignableTicket)}
                                    onClickAction={setTicketKeyToAssociate}
                                />
                                {index === searchedAssociableTicketList.length - 1 ? null : (
                                    <Divider
                                        style={styles.zeroMargin}
                                        key={getTicketKey(assignableTicket) + 'associable-divider'}
                                    />
                                )}
                            </div>
                        );
                    })
                ) : (
                    <Empty />
                )}
                {ticketKeyToAssociate !== emptyTicketKeyValue ? (
                    <>
                        <Divider style={styles.divider} />
                        <Row justify="center" style={styles.button}>
                            <Button type="primary" onClick={handleOnClick} loading={loading} ref={buttonRef}>
                                {buttonText}
                            </Button>
                        </Row>
                    </>
                ) : null}
            </Space>
        </div>
    );
};

export default observer(TicketAssociationContainer);
